import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Box, Button, Checkbox } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import {
  COLORS,
  Label14Medium,
  Label16SemiBold,
  Loading,
  SettingButtonText,
  SettingPrimaryLabel,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import MultiSelectComponent from "components/inputs/multiSelect-input";
import { addCustomSiteType, AllowedValuesDTO, getCustomSiteType, removeCustomSiteType } from "api";
import { useNotify } from "hooks";
import { Option } from "./types";

export const CustomSiteTypes = ({ allowedValues }: { allowedValues: AllowedValuesDTO }) => {
  const notify = useNotify();

  const [checked, setChecked] = useState(false);
  const [selectedSites, setSelectedSites] = useState<Option[]>([]);
  const [currentSiteType, setCurrentSiteType] = useState<any>();

  const {
    data: customSiteType,
    refetch: refetchCustomSites,
    isLoading: isLoadingCustomSites
  } = useQuery({
    queryKey: ["customSiteType"],
    queryFn: getCustomSiteType,
  });

  const { mutateAsync: saveCustomSiteType, isLoading: isSaving } = useMutation(addCustomSiteType);
  const { mutateAsync: removeCustom } = useMutation(removeCustomSiteType);

  const siteTypeOptions = useMemo(() => allowedValues?.siteTypes?.map((site: any) => ({
    value: site,
    label: site,
    name: site,
  })) ?? [], [allowedValues]);

  const handleSaveChange = async () => {
    try {
      const values = selectedSites.map((item) => (typeof item === "string" ? item : item.name));
      await saveCustomSiteType({ body: values, keepDefaultSiteType: checked });
      notify.success("Successfully updated Custom Site Types!");
      refetchCustomSites();
    } catch (error) {
      notify.error("Failed to save Custom Site Types.");
    }
  };

  const handleChipRemove = async (option: Option) => {
    try {
      await removeCustom(option);
      notify.success(`Removed ${option.name}`);
      refetchCustomSites();
    } catch {
      notify.error("Error removing custom site type.");
    }
  };

  useEffect(() => {
    if (customSiteType) {
      setSelectedSites(customSiteType);
    }
  }, [customSiteType]);

  const isLoading = isLoadingCustomSites || isSaving;

  if (isLoading) return <Loading />;

  return (
    <Box sx={{ display: "flex", gap: 6 }}>
      <Box sx={{ minWidth: "240px" }}>
        {siteTypeOptions.length > 0 && (
          <>
            <Label16SemiBold sx={{ my: 1 }}>Custom Site Types</Label16SemiBold>
            <EsgMultiselectControlledField
              selectedValues={currentSiteType}
              setSelectedValues={setCurrentSiteType}
              options={siteTypeOptions}
              isAllSelectedOnEmptySelection
              type="rounded"
            />
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Label16SemiBold sx={{ my: 1 }}>Use Custom Site Types</Label16SemiBold>
        <SettingPrimaryLabel sx={{ mb: 1 }}>
          Please Press Enter to add more custom sites.
        </SettingPrimaryLabel>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Checkbox
            color="primary"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            sx={{ p: 0 }}
          />
          <Label14Medium sx={{ ml: 1 }}>
            Keep all default site types in addition to my custom sites
          </Label14Medium>
        </Box>

        <MultiSelectComponent
          selectedValues={selectedSites}
          onChange={setSelectedSites}
          onRemove={handleChipRemove}
          placeholder="Custom Sites"
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveChange}
            disabled={isSaving}
            sx={{
              color: "white",
              backgroundColor: COLORS.Romance,
              width: "168px",
              height: "36px",
              borderRadius: "8px"
            }}
          >
            <SettingButtonText>Save Changes</SettingButtonText>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
